import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import CartQuantityButtons from "../../components/common/CartQuantityButtons";
import { useState } from "react";

function ParentComboProduct({
  isLoading,
  cartLength,
  index,
  parentProduct,
  childProducts,
  updateCartQtyMutate,
}) {
  const [expanded, setExpanded] = useState(false);
  const quantity = childProducts?.[0]?.product_quantity || 1;

  const unitPrice = parentProduct?.combo_price;
  const originalComboProductPrice = childProducts?.reduce(
    (total, item) =>
      total +
      (+item?.variations?.egg > 0
        ? +item?.variations?.egg
        : +item?.variations?.eggLess || 0),
    0,
  );

  // ✅ Check if parent has addons
  const parentAddons = parentProduct?.parent_addons || [];
  const hasAddons = parentAddons.length > 0;

  // ✅ Calculate total addon price (base price, before quantity multiplication)
  const baseAddonPrice =
    parentAddons.reduce((total, addon) => total + (+addon.total || 0), 0) || 0;

  // ✅ Multiply addon price by quantity
  const totalAddonPrice = baseAddonPrice * quantity;

  // ✅ Calculate final prices with addons included and multiplied by quantity
  const discountPrice = (+unitPrice * quantity).toFixed(2);
  const originalPrice = (
    (+originalComboProductPrice + baseAddonPrice) *
    quantity
  ).toFixed(2);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Parent product row */}
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{
          boxShadow: "none",
          mb: 2,
          borderRadius: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`combo-content-${index}`}
          id={`combo-header-${index}`}
          sx={{
            minHeight: 56,
            background: "white",
            borderRadius: 1,
            padding:0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <CartImage
                src={parentProduct?.app_image}
                alt={parentProduct?.product_name}
              />
            </Box>
            <Box sx={{ flexGrow: 1, alignSelf: "flex-start" }}>
              <CakeName>{parentProduct?.product_name}</CakeName>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "150%",
                    color: "var(--neutral-black)",
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  ₹ {discountPrice}
                </Typography>
                {discountPrice !== originalPrice && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "150%",
                      color: "var(--neutral-700)",
                      textAlign: { xs: "center", md: "left" },
                      textDecoration: "line-through",
                    }}
                  >
                    ₹ {originalPrice}
                  </Typography>
                )}
                <ComboTag />
              </Stack>
              {isLoading ? (
                <Stack direction="row" spacing={2} alignItems={"center"}>
                  <Skeleton
                    variant="rectangular"
                    width={24}
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={24}
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={24}
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                </Stack>
              ) : (
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: 1, md: 0 },
                    mt: 2,
                  }}
                >
                  <CartQuantityButtons
                    updateCartQtyMutate={updateCartQtyMutate}
                    cartId={childProducts?.[0]?.cart_id}
                    customerId={childProducts?.[0]?.customer_id}
                    currentQty={childProducts?.[0]?.product_quantity}
                    parentId={parentProduct?.id}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            background: "var(--neutral-white)",
            gap: 2,
            flexWrap: "wrap",
            display: "flex",
          }}
        >
          {/* ✅ Child products FIRST */}
          {childProducts.map((cart, idx) => {
            const childCartPrice =
              cart.variations?.egg > 0
                ? cart.variations?.egg * quantity
                : cart.variations?.eggLess * quantity;
            return (
              <Box
                key={cart.cart_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  borderRadius: 1,
                  background: "var(--neutral-100)",
                  width: {
                    xs: "100%",
                    sm: "100%",
                    md: "48%",
                  },
                  border: `1px solid var(--neutral-200)`,
                  py: 1,
                  px: 2,
                }}
              >
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <CartImage
                    src={cart?.products?.app_image?.app_product_image}
                    alt={cart?.products?.product_name}
                    style={{ width: 70, height: 70 }}
                  />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <CakeName
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {cart?.products?.product_name}
                  </CakeName>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent={"space-between"}
                    sx={{ mt: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "var(--neutral-500)", fontSize: "14px" }}
                    >
                      {cart?.variations?.weight[0]?.weight_name} &nbsp;
                      {cart?.products?.unit?.unit_name}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "var(--neutral-black)",
                        mt: 0.5,
                        fontSize: "15px",
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      ₹ {childCartPrice}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })}

          {/* ✅ Addons SECOND - with quantity multiplied */}
          {hasAddons &&
            parentAddons.map((addon, idx) => {
              // ✅ Multiply addon total by parent quantity
              const addonTotalPrice = +addon.total * quantity;

              return (
                <Box
                  key={addon.id || idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 1,
                    background: "var(--neutral-100)",
                    width: {
                      xs: "100%",
                      sm: "100%",
                      md: "48%",
                    },
                    border: `1px solid var(--neutral-200)`,
                    py: 1,
                    px: 2,
                  }}
                >
                  <CartImage
                    src={addon.image}
                    alt={addon.product_name}
                    style={{ width: 70, height: 70 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <CakeName
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {addon.product_name}
                    </CakeName>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={"space-between"}
                      sx={{ mt: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "var(--neutral-500)", fontSize: "14px" }}
                      >
                        {/* Qty: {addon.quantity} × {quantity} */}
                        Qty: {addon.quantity}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "var(--neutral-black)",
                          mt: 0.5,
                          fontSize: "15px",
                          textAlign: { xs: "center", md: "left" },
                        }}
                      >
                        ₹ {addonTotalPrice}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
        </AccordionDetails>
      </Accordion>
      {index !== cartLength - 1 && <Divider variant="middle" sx={{ my: 3 }} />}
    </Box>
  );
}

const CartImage = styled("img")(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  textAlign: "center",
  width: "144px",
  borderRadius: "8px",
  cursor: "pointer",
  objectFit: "cover",
  [theme.breakpoints.down("sm")]: {
    width: "80px !important",
    height: "80px !important",
  },
}));
const ComboTag = () => {
  const StyledComboTag = styled("span")(({ theme }) => ({
    backgroundColor: "#dc2626",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.75rem",
    padding: "0.2rem 0.5rem",
    borderRadius: "0.375rem",
    marginLeft: "8px",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    display: "inline-block",
    userSelect: "none",
  }));

  return <StyledComboTag>COMBO</StyledComboTag>;
};
const CakeName = styled("h3")(({ theme }) => ({
  margin: 0,
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "150%",
  color: "var(--neutral-black)",
  textAlign: "left",
  textTransform: "capitalize",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px !important",
  },
}));
export default ParentComboProduct;
